import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  type RouteObject,
} from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireWallet, AdminOnly } from '@/components/auth';
import { ROUTES } from '@/config/routes';
import { RouteErrorPage } from './RouteErrorPage';

// Build-time variant (statically replaced by Vite): in the general-user build
// every admin-only route below is dead-code-eliminated, so their lazy chunks
// (AI chat, data explorer, strategies) are never even emitted — the two builds
// are effectively two apps. See src/config/appVariant.ts.
const IS_ADMIN_BUILD = import.meta.env.VITE_APP_VARIANT === 'admin';

// Lazy-loaded pages shared by BOTH builds
const ManagedWalletOnboarding = lazy(() => import('@/pages/Onboarding/ManagedWalletOnboarding'));
const TokenPage = lazy(() => import('@/pages/Token/TokenPage'));
const BacktestTokenPage = lazy(() => import('@/pages/Backtest/BacktestTokenPage'));

// Trading Suite pages
const TrendingDashboard = lazy(() => import('@/pages/Trading').then(m => ({ default: m.TrendingDashboard })));
const GoodTradersDiscovery = lazy(() => import('@/pages/Trading').then(m => ({ default: m.GoodTradersDiscovery })));
const GoodTradersForToken = lazy(() => import('@/pages/Trading').then(m => ({ default: m.GoodTradersForToken })));
const PositionsOverview = lazy(() => import('@/pages/Trading').then(m => ({ default: m.PositionsOverview })));
const TokenPositionDetail = lazy(() => import('@/pages/Trading').then(m => ({ default: m.TokenPositionDetail })));
const WalletHoldings = lazy(() => import('@/pages/Trading').then(m => ({ default: m.WalletHoldings })));

// Settings
const Settings = lazy(() => import('@/pages/Settings').then((m) => ({ default: m.Settings })));

// Trading home (overview). Imported from its file — NOT the Strategies barrel —
// so the user build doesn't drag the whole strategies suite (editor, elk) into
// the bundle through the barrel's re-exports.
const TradingOverview = lazy(() =>
  import('@/pages/Strategies/TradingOverview').then(m => ({ default: m.TradingOverview }))
);

/**
 * Loading fallback component
 */
const LoadingFallback = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="spinner" />
  </div>
);

const suspense = (node: ReactNode) => <Suspense fallback={<LoadingFallback />}>{node}</Suspense>;

/**
 * Admin-only routes: AI chat, the data explorer (browse pages, not token
 * detail), and the strategies suite. The lazy() calls live inside the guarded
 * block so the user build drops their chunks entirely.
 */
const adminRoutes: RouteObject[] = IS_ADMIN_BUILD
  ? (() => {
      const AIChat = lazy(() => import('@/pages/AIChat/AIChat'));
      const DataExplorer = lazy(() => import('@/pages/DataExplorer/DataExplorer'));
      const TokenExplorer = lazy(() => import('@/pages/DataExplorer/TokenExplorer'));
      const SolTokenExplorer = lazy(() => import('@/pages/DataExplorer/SolTokenExplorer'));
      const WalletProfile = lazy(() => import('@/pages/DataExplorer/WalletProfile'));
      const MyLists = lazy(() => import('@/pages/DataExplorer/MyLists'));
      const ListDetail = lazy(() => import('@/pages/DataExplorer/ListDetail'));
      const StrategiesList = lazy(() => import('@/pages/Strategies').then(m => ({ default: m.StrategiesList })));
      const StrategyDetail = lazy(() => import('@/pages/Strategies/StrategyDetail'));
      const StrategyTokenTrades = lazy(() => import('@/pages/Strategies').then(m => ({ default: m.StrategyTokenTrades })));
      const VisualFlowEditor = lazy(() => import('@/pages/Strategies').then(m => ({ default: m.VisualFlowEditor })));
      const StrategyTemplates = lazy(() => import('@/pages/Strategies').then(m => ({ default: m.StrategyTemplates })));
      const AdminStrategyPerformance = lazy(() => import('@/pages/Strategies/AdminStrategyPerformance'));

      // Runtime guard within the admin build: a non-admin account signing into
      // the admin deployment is redirected home (and the API 403s regardless).
      const adminOnly = (element: ReactNode) => (
        <AdminOnly fallback={<Navigate to={ROUTES.TRADING.ROOT} replace />}>{element}</AdminOnly>
      );

      return [
        {
          path: ROUTES.TRADING.STRATEGIES,
          element: adminOnly(<RequireWallet>{suspense(<StrategiesList />)}</RequireWallet>),
        },
        {
          // Cross-user performance overview; read-only, so no RequireWallet
          path: ROUTES.TRADING.ADMIN_STRATEGY_PERFORMANCE,
          element: adminOnly(suspense(<AdminStrategyPerformance />)),
        },
        {
          path: ROUTES.TRADING.STRATEGY_DETAIL,
          element: adminOnly(<RequireWallet>{suspense(<StrategyDetail />)}</RequireWallet>),
        },
        {
          path: ROUTES.TRADING.STRATEGY_TOKEN_TRADES,
          element: adminOnly(<RequireWallet>{suspense(<StrategyTokenTrades />)}</RequireWallet>),
        },
        {
          path: ROUTES.TRADING.STRATEGY_EDITOR,
          element: adminOnly(<RequireWallet>{suspense(<VisualFlowEditor />)}</RequireWallet>),
        },
        {
          path: ROUTES.TRADING.STRATEGY_TEMPLATES,
          element: adminOnly(<RequireWallet>{suspense(<StrategyTemplates />)}</RequireWallet>),
        },
        {
          path: ROUTES.AI_CHAT.SETTINGS,
          element: <Navigate to={ROUTES.SETTINGS} replace />,
        },
        {
          path: ROUTES.AI_CHAT.ROOT,
          element: adminOnly(suspense(<AIChat />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.ROOT,
          element: adminOnly(suspense(<DataExplorer />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.CHAIN_ROOT,
          element: adminOnly(suspense(<DataExplorer />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.TOKEN_EXPLORER,
          element: adminOnly(suspense(<TokenExplorer />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.SOL_TOKEN_EXPLORER,
          element: adminOnly(suspense(<SolTokenExplorer />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.CHAIN_TOKEN_EXPLORER,
          element: adminOnly(suspense(<TokenExplorer />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.WALLET_PROFILE,
          element: adminOnly(suspense(<WalletProfile />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.CHAIN_WALLET_PROFILE,
          element: adminOnly(suspense(<WalletProfile />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.MY_LISTS,
          element: adminOnly(suspense(<MyLists />)),
        },
        {
          path: ROUTES.DATA_EXPLORER.LIST_DETAIL,
          element: adminOnly(suspense(<ListDetail />)),
        },
      ];
    })()
  : [];

/**
 * Router configuration
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.TRADING.ROOT} replace />,
      },
      {
        path: ROUTES.ONBOARDING,
        element: suspense(<ManagedWalletOnboarding />),
      },
      {
        path: ROUTES.SETTINGS,
        element: suspense(<Settings />),
      },
      {
        path: ROUTES.TRADING.ROOT,
        element: <RequireWallet>{suspense(<TradingOverview />)}</RequireWallet>,
      },
      {
        path: ROUTES.TRADING.DASHBOARD,
        element: suspense(<TrendingDashboard />),
      },
      {
        path: ROUTES.TRADING.GOOD_TRADERS,
        element: suspense(<GoodTradersDiscovery />),
      },
      {
        path: ROUTES.TRADING.GOOD_TRADERS_TOKEN,
        element: suspense(<GoodTradersForToken />),
      },
      {
        path: ROUTES.TRADING.POSITIONS,
        element: suspense(<PositionsOverview />),
      },
      {
        path: ROUTES.TRADING.POSITION_TOKEN,
        element: suspense(<TokenPositionDetail />),
      },
      {
        path: ROUTES.TRADING.HOLDINGS,
        element: suspense(<WalletHoldings />),
      },
      ...adminRoutes,
      // Token detail stays in BOTH builds: general users reach token pages by
      // clicking a token directly (dashboard rows, holdings, search) — never
      // by browsing the explorer.
      {
        path: ROUTES.DATA_EXPLORER.TOKEN_DETAIL,
        element: suspense(<TokenPage />),
      },
      {
        path: ROUTES.DATA_EXPLORER.CHAIN_TOKEN_DETAIL,
        element: suspense(<TokenPage />),
      },
      {
        path: 'backtest/:runId/:tokenAddress',
        element: suspense(<BacktestTokenPage />),
      },
      // Anything unmatched — including admin-only URLs in the user build —
      // lands back on the trading overview.
      {
        path: '*',
        element: <Navigate to={ROUTES.TRADING.ROOT} replace />,
      },
    ],
  },
]);

/**
 * App router component
 */
export const AppRouter = () => <RouterProvider router={router} />;
